/**
 * §P.5 · Editar la escalera de cinturones.
 *
 * ## El `PUT` manda la escalera entera, y lo que falta se borra
 *
 * No es un formulario por cinturón: es uno solo que sustituye la lista completa.
 * Eso tiene dos consecuencias que hay que respetar o se pierde historial:
 *
 * **El `id` viaja de vuelta.** Con `id` se actualiza ese cinturón; sin él se crea
 * uno nuevo. Si la pantalla mandara la lista sin los identificadores, el servidor
 * borraría los diez cinturones y crearía diez nuevos — y el historial de cada
 * alumno apunta al identificador del suyo. «Verde desde 2019» se convertiría en un
 * grado huérfano.
 *
 * **Quitar una fila borra ese cinturón.** Si lo lleva alguien puesto, el servidor
 * responde `409 rank_in_use` en lugar de dejar caer un error de clave foránea, y la
 * pantalla dice cuántos lo llevan. Por eso el botón de quitar no aparece en los
 * cinturones que están en uso: el servidor lo impide igual, pero un botón que
 * siempre falla es un botón que no debería estar.
 *
 * ## Los requisitos son para el siguiente grado
 *
 * Los cuatro `min_*` de un cinturón dicen qué hace falta para examinarse **del
 * siguiente**, no de este. Así los lee `v_exam_eligibility`. La cabecera de la
 * columna lo dice con esas palabras porque rellenarlo al revés desplaza la
 * exigencia de todo el sistema un peldaño.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { Cinturon } from '@/club/grados/Cinturon';
import {
  aFraccion,
  comoPorcentaje,
  nombreDeCinturon,
  type Cinturon as TipoDeCinturon,
} from '@/club/grados/tipos';

/** Una fila del editor. `id` nulo es un cinturón nuevo. Ver la cabecera. */
interface Fila {
  id: string | null;
  name: string;
  short_name: string;
  color_hex: string;
  color_hex_2: string;
  is_dan: boolean;
  max_stripes: string;
  meses: string;
  asistencia: string;
  clases: string;
  edad: string;
  /** Solo para menores de esta edad: el Pum, el cinto negro juvenil. */
  hasta: string;
  /** Solo informativo: cuántos lo llevan. No se manda. */
  students_with_it: number;
}

const desde = (c: TipoDeCinturon): Fila => ({
  id: c.id,
  name: c.name,
  short_name: c.short_name ?? '',
  color_hex: c.color_hex,
  color_hex_2: c.color_hex_2 ?? '',
  is_dan: c.is_dan,
  max_stripes: String(c.max_stripes),
  meses:
    c.min_months_since_last === null ? '' : String(c.min_months_since_last),
  asistencia: comoPorcentaje(c.min_attendance_rate),
  clases: c.min_classes === null ? '' : String(c.min_classes),
  edad: c.min_age === null ? '' : String(c.min_age),
  hasta: c.max_age === null ? '' : String(c.max_age),
  students_with_it: c.students_with_it,
});

const nueva = (): Fila => ({
  id: null,
  name: '',
  short_name: '',
  color_hex: '#ffffff',
  color_hex_2: '',
  is_dan: false,
  max_stripes: '0',
  meses: '',
  asistencia: '',
  clases: '',
  edad: '',
  hasta: '',
  students_with_it: 0,
});

/** Texto → entero, o `null` si está vacío. `undefined` es «no se entiende». */
function aEntero(texto: string): number | null | undefined {
  const limpio = texto.trim();
  if (limpio === '') return null;
  const n = Number.parseInt(limpio, 10);
  return Number.isFinite(n) && n >= 0 && String(n) === limpio ? n : undefined;
}

export function EscaleraDeGrados({
  club,
  schemeId,
  alCerrar,
}: {
  club: string;
  schemeId: string;
  alCerrar: () => void;
}) {
  const cache = useQueryClient();
  const [filas, setFilas] = useState<Fila[] | null>(null);
  const [sembradoDe, setSembradoDe] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);
  const [enUso, setEnUso] = useState<Problema | null>(null);

  const cinturones = useQuery({
    queryKey: ['cinturones', club, schemeId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId })
        .ranks.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as TipoDeCinturon[];
    },
  });

  /*
   * El formulario se siembra **durante el render**, no desde un efecto.
   *
   * Es el patrón de «ajustar el estado cuando cambia una prop» que React
   * recomienda: un `useEffect` que llama a `setState` provoca un render de más y
   * pinta una vez con el estado viejo. Aquí eso sería la escalera vacía por un
   * fotograma.
   *
   * `sembradoDe` es la clave: al cambiar de sistema hay que volver a sembrar, y con
   * un `filas === null` a secas el editor se quedaría con los cinturones del
   * sistema anterior.
   */
  if (cinturones.data && sembradoDe !== schemeId) {
    setSembradoDe(schemeId);
    setFilas(cinturones.data.map(desde));
  }

  const guardar = useMutation({
    mutationFn: async (lista: Fila[]) => {
      const ranks = lista.map((f, i) => {
        const meses = aEntero(f.meses);
        const clases = aEntero(f.clases);
        const edad = aEntero(f.edad);
        const hasta = aEntero(f.hasta);
        const franjas = aEntero(f.max_stripes);
        const asistencia = aFraccion(f.asistencia);

        if (
          meses === undefined ||
          clases === undefined ||
          edad === undefined ||
          hasta === undefined ||
          franjas === undefined ||
          asistencia === undefined
        ) {
          throw new ErrorDeApi({
            title: `Hay algo que no se entiende en «${f.name || 'un cinturón'}»`,
            status: 400,
            detail:
              'Los meses, las clases, la edad y las franjas son números enteros; la asistencia, un porcentaje de 0 a 100.',
          });
        }

        return {
          // **El `id` de vuelta.** Sin él el servidor borra y crea, y el historial
          // de cada alumno se queda apuntando a un cinturón que ya no existe.
          ...(f.id ? { id: f.id } : {}),
          sort_order: i,
          name: f.name.trim(),
          short_name: f.short_name.trim() === '' ? null : f.short_name.trim(),
          color_hex: f.color_hex,
          color_hex_2: f.color_hex_2 === '' ? null : f.color_hex_2,
          is_dan: f.is_dan,
          max_stripes: franjas ?? 0,
          min_months_since_last: meses,
          min_attendance_rate: asistencia,
          min_classes: clases,
          min_age: edad,
          max_age: hasta,
        };
      });

      const r = await api.v1
        .clubs({ club })
        ['belt-schemes']({ schemeId })
        .ranks.put({ ranks });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['cinturones', club] });
      void cache.invalidateQueries({ queryKey: ['grados', club] });
      alCerrar();
    },
    onError: (e) => {
      const p = aProblema(e);
      if (p.code === 'rank_in_use') setEnUso(p);
      else setProblema(p);
    },
  });

  if (cinturones.isPending || filas === null) {
    return <Cargando filas={6} etiqueta="Cargando la escalera" />;
  }

  if (cinturones.isError) {
    return (
      <PanelError
        problema={aProblema(cinturones.error)}
        reintentar={() => void cinturones.refetch()}
      />
    );
  }

  const cambiar = (i: number, campo: keyof Fila, valor: string | boolean) =>
    setFilas((f) =>
      (f ?? []).map((fila, j) => (j === i ? { ...fila, [campo]: valor } : fila))
    );

  const mover = (i: number, delta: number) =>
    setFilas((f) => {
      const lista = [...(f ?? [])];
      const destino = i + delta;
      if (destino < 0 || destino >= lista.length) return lista;
      const [fila] = lista.splice(i, 1);
      lista.splice(destino, 0, fila!);
      return lista;
    });

  const quitar = (i: number) =>
    setFilas((f) => (f ?? []).filter((_, j) => j !== i));

  return (
    <section>
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Grados
        </button>
      </div>

      <h1>La escalera de cinturones</h1>

      <div className="nota">
        <strong>
          Los requisitos de cada fila son para subir al siguiente.
        </strong>{' '}
        Los del blanco dicen qué hace falta para examinarse de amarillo, no para
        tener el blanco. Es como los lee el sistema de exámenes.
      </div>

      {enUso && (
        <div className="aviso" role="status">
          <strong>{enUso.title}</strong> {enUso.detail}
          <p className="tenue">
            Un cinturón que alguien lleva puesto no se puede borrar: su
            historial apunta aquí. Si ya no se usa, cámbiale el nombre en vez de
            quitarlo.
          </p>
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      <div className="tabla-marco">
        <table className="tabla escalera-editor">
          <thead>
            <tr>
              <th>Orden</th>
              <th>Cinturón</th>
              <th>Colores</th>
              <th>Franjas</th>
              <th colSpan={4}>Para subir al siguiente</th>
              <th>Solo menores</th>
              <th />
            </tr>
            <tr>
              <th colSpan={4} />
              <th>Meses</th>
              <th>Asistencia</th>
              <th>Clases</th>
              <th>Edad</th>
              <th>de (años)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr key={f.id ?? `nuevo-${i}`}>
                <td>
                  <div className="tutor-acciones" style={{ margin: 0 }}>
                    <button
                      type="button"
                      className="btn-plano"
                      aria-label={`Subir ${f.name || 'este cinturón'}`}
                      disabled={i === 0}
                      onClick={() => mover(i, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-plano"
                      aria-label={`Bajar ${f.name || 'este cinturón'}`}
                      disabled={i === filas.length - 1}
                      onClick={() => mover(i, 1)}
                    >
                      ↓
                    </button>
                  </div>
                </td>

                <td>
                  <input
                    required
                    maxLength={80}
                    placeholder="Amarillo"
                    value={f.name}
                    onChange={(e) => cambiar(i, 'name', e.target.value)}
                  />
                  <input
                    maxLength={20}
                    placeholder="Abreviado"
                    value={f.short_name}
                    onChange={(e) => cambiar(i, 'short_name', e.target.value)}
                  />
                </td>

                <td>
                  <div className="colores">
                    <input
                      type="color"
                      aria-label="Color del cinturón"
                      value={f.color_hex}
                      onChange={(e) => cambiar(i, 'color_hex', e.target.value)}
                    />
                    {/*
                      El segundo color es opcional y por eso lleva su casilla: un
                      `<input type="color">` no puede estar vacío —siempre tiene un
                      valor— así que sin la casilla no habría forma de decir «liso».
                    */}
                    <label className="opcion">
                      <input
                        type="checkbox"
                        checked={f.color_hex_2 !== ''}
                        onChange={(e) =>
                          cambiar(
                            i,
                            'color_hex_2',
                            e.target.checked ? '#ffffff' : ''
                          )
                        }
                      />
                      Bicolor
                    </label>
                    {f.color_hex_2 !== '' && (
                      <input
                        type="color"
                        aria-label="Segundo color"
                        value={f.color_hex_2}
                        onChange={(e) =>
                          cambiar(i, 'color_hex_2', e.target.value)
                        }
                      />
                    )}
                  </div>
                  <Cinturon
                    color={f.color_hex}
                    color2={f.color_hex_2 === '' ? null : f.color_hex_2}
                    nombre={nombreDeCinturon({
                      name: f.name || 'Sin nombre',
                      short_name: f.short_name,
                    })}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    aria-label="Franjas como mucho"
                    value={f.max_stripes}
                    onChange={(e) => cambiar(i, 'max_stripes', e.target.value)}
                  />
                </td>

                <td>
                  <input
                    inputMode="numeric"
                    placeholder="—"
                    aria-label="Meses desde el grado anterior"
                    value={f.meses}
                    onChange={(e) => cambiar(i, 'meses', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    inputMode="numeric"
                    placeholder="—"
                    aria-label="Asistencia mínima, en porcentaje"
                    value={f.asistencia}
                    onChange={(e) => cambiar(i, 'asistencia', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    inputMode="numeric"
                    placeholder="—"
                    aria-label="Clases como mínimo"
                    value={f.clases}
                    onChange={(e) => cambiar(i, 'clases', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    inputMode="numeric"
                    placeholder="—"
                    aria-label="Edad mínima"
                    value={f.edad}
                    onChange={(e) => cambiar(i, 'edad', e.target.value)}
                  />
                </td>
                <td>
                  {/*
                    El Pum y el cinto negro juvenil: el grado deja de darse a esta
                    edad, y quien ya la tiene pasa directo al siguiente. Vacío en el
                    resto.
                  */}
                  <input
                    inputMode="numeric"
                    placeholder="—"
                    aria-label="Solo para menores de esta edad"
                    value={f.hasta}
                    onChange={(e) => cambiar(i, 'hasta', e.target.value)}
                  />
                </td>

                <td>
                  {/*
                    Sin botón de quitar si lo lleva alguien: el servidor responde
                    `409` igual, pero un botón que siempre falla no debería estar.
                  */}
                  {f.students_with_it === 0 ? (
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => quitar(i)}
                    >
                      Quitar
                    </button>
                  ) : (
                    <span className="tenue">
                      {f.students_with_it === 1
                        ? 'lo lleva 1'
                        : `lo llevan ${f.students_with_it}`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="acciones">
        <button
          type="button"
          disabled={guardar.isPending}
          onClick={() => {
            setProblema(null);
            setEnUso(null);
            guardar.mutate(filas);
          }}
        >
          {guardar.isPending ? 'Guardando…' : 'Guardar la escalera'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={() => setFilas([...filas, nueva()])}
        >
          Añadir un cinturón
        </button>
        <button type="button" className="btn-plano" onClick={alCerrar}>
          Cancelar
        </button>
      </div>

      <p className="tenue">
        Se guarda la escalera entera de una vez. Un cinturón que quites aquí se
        borra, y por eso los que alguien lleva puesto no se pueden quitar: su
        historial apunta a este cinturón y se quedaría sin sitio.
      </p>
    </section>
  );
}
